/**
 * Decisions & Actions log modal — the facilitator-facing surface behind
 * the Decision Traceability non-negotiable (NN3) + action capture
 * (P2-US3). Two sections in one modal:
 *
 *   - **Decisions** — title + rationale ("why") + owner + status
 *     (proposed / agreed / parked). The rationale field is the whole
 *     point: it's the record that answers "why did we decide this?".
 *   - **Actions** — title + owner + due + status (open / in-progress /
 *     done).
 *
 * Both are backed by per-workshop IDB stores (`useDecisions` /
 * `useActions`) so a real facilitator can capture, edit, and persist
 * their own records — they survive reload, independent of any GitHub
 * PAT. Reuses the `.modal-shell` chrome + `PanelDialogContent` from the
 * WS4 output modals (see `QuestionnaireModal` for the template), and
 * the shared `useWorkshopModals` open/close store.
 */

import { useEffect, useMemo, useState, type CSSProperties } from "react";
import { ClipboardCheck, FlaskConical, ListChecks, Scale, X } from "lucide-react";
import { Dialog, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { PanelDialogContent } from "@/components/shell/PanelDialogContent";
import { cn } from "@/lib/utils";
import { StatusSelect, DueDatePicker, OwnerCombobox } from "./log-controls";
import { FIELD_CLASS, formatDueDate, statusAccent } from "./log-controls.utils";
import {
  AddButton,
  EditableTitle,
  EmptyHint,
  RemoveButton,
  SectionLabel,
} from "./log-rows";
import {
  AiExtractionSection,
  FindingsSection,
  NotesSection,
  OpportunitiesSection,
  RecommendationsSection,
  RowTags,
  useHydrateRegisters,
} from "./register-sections";
import { TagInput } from "@/components/tags/TagInput";
import { WorkshopLabelPill, workshopLabelColor } from "@/components/tags/WorkshopLabelPill";
import { collectTags } from "@/lib/tags";
import { useFindings } from "@/stores/useFindings";
import { useOpportunities } from "@/stores/useOpportunities";
import { useRecommendations } from "@/stores/useRecommendations";
import { useFacilitationNotes } from "@/stores/useFacilitationNotes";
import { useAuth } from "@/hooks/use-auth";
import type { Workshop } from "@/content/workshops";
import {
  useJumpTarget,
  logTabForCategory,
} from "@/features/search/useJumpTarget";
import {
  useDecisions,
  type Decision,
  type DecisionStatus,
} from "@/stores/useDecisions";
import {
  useActions,
  type ActionItem,
  type ActionStatus,
} from "@/stores/useActions";
import {
  useAssumptions,
  type Assumption,
  type AssumptionStatus,
} from "@/stores/useAssumptions";

type DecisionLogModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workshopId: string;
};

/** Tabbed Workshop Log groups (LOG-TABS):
 *   core      = Decisions / Actions / Assumptions (R1)
 *   knowledge = Findings / Opportunities / Recommendations (Track A R2)
 *   notes     = Facilitation notes (statusless) */
type LogTab = "core" | "knowledge" | "notes";

const LOG_TABS: ReadonlyArray<{ key: LogTab; label: string }> = [
  { key: "core", label: "Decisions · Actions · Assumptions" },
  { key: "knowledge", label: "Findings · Opportunities · Recs" },
  { key: "notes", label: "Notes" },
];

const DECISION_STATUSES: DecisionStatus[] = ["proposed", "agreed", "parked"];
const ACTION_STATUSES: ActionStatus[] = ["open", "in-progress", "done"];
const ASSUMPTION_STATUSES: AssumptionStatus[] = [
  "assumed",
  "validated",
  "refuted",
];

const EMPTY_DECISIONS: readonly Decision[] = Object.freeze([]);
const EMPTY_ACTIONS: readonly ActionItem[] = Object.freeze([]);
const EMPTY_ASSUMPTIONS: readonly Assumption[] = Object.freeze([]);

// Row chrome — inline styles (the codebase favours inline `var(--*)`
// styles over bespoke utility classes in these feature components).
const ROW_CARD: CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  gap: 10,
  padding: "8px 10px",
  borderRadius: 8,
  background: "rgba(var(--accent-true-blue-rgb), 0.04)",
  border: "1px solid rgba(var(--accent-true-blue-rgb), 0.12)",
};
const ROW_TITLE: CSSProperties = {
  fontSize: "var(--fs-base)",
  fontWeight: 600,
  color: "var(--text-primary)",
  overflow: "hidden",
  textOverflow: "ellipsis",
};
const ROW_SUB: CSSProperties = {
  fontSize: "var(--fs-label)",
  color: "var(--text-secondary)",
  marginTop: 1,
};
const ROW_META: CSSProperties = {
  fontSize: "var(--fs-label)",
  color: "var(--text-muted)",
  letterSpacing: "0.3px",
  marginTop: 2,
};

export function DecisionLogModal({
  open,
  onOpenChange,
  workshopId,
}: DecisionLogModalProps) {
  const wsId = workshopId as Workshop["id"];
  const auth = useAuth();
  const addedBy = auth.user?.displayName ?? auth.user?.username ?? "anon";

  // Owner combobox options — roster display names, deduped + sorted. The
  // owner inputs stay free-text (type a non-roster owner) but suggest
  // known crew via the themed <OwnerCombobox> typeahead.
  const ownerOptions = useMemo(
    () =>
      Array.from(
        new Set(auth.users.map((u) => u.displayName).filter(Boolean)),
      ).sort((a, b) => a.localeCompare(b)),
    [auth.users],
  );

  const decisions = useDecisions(
    (s) => s.entriesByWorkshop[wsId] ?? (EMPTY_DECISIONS as Decision[]),
  );
  const addDecision = useDecisions((s) => s.add);
  const updateDecision = useDecisions((s) => s.update);
  const removeDecision = useDecisions((s) => s.remove);

  const actions = useActions(
    (s) => s.entriesByWorkshop[wsId] ?? (EMPTY_ACTIONS as ActionItem[]),
  );
  const addAction = useActions((s) => s.add);
  const updateAction = useActions((s) => s.update);
  const removeAction = useActions((s) => s.remove);

  const assumptions = useAssumptions(
    (s) => s.entriesByWorkshop[wsId] ?? (EMPTY_ASSUMPTIONS as Assumption[]),
  );
  const addAssumption = useAssumptions((s) => s.add);
  const updateAssumption = useAssumptions((s) => s.update);
  const removeAssumption = useAssumptions((s) => s.remove);

  // Lazy hydrate on first open — these stores aren't hydrated at app
  // boot (unlike captures/asset-locker), so prime them when the modal
  // is first shown. `hydrate()` is idempotent (guarded by isHydrated).
  useEffect(() => {
    if (!open) return;
    void useDecisions.getState().hydrate();
    void useActions.getState().hydrate();
    void useAssumptions.getState().hydrate();
  }, [open]);

  // Track A registers (Findings / Opportunities / Recommendations / Notes):
  // hydrate on open + count for the tab badge. The sections themselves read
  // and mutate the stores.
  useHydrateRegisters(open);
  const findingCount = useFindings((s) => s.entriesByWorkshop[wsId]?.length ?? 0);
  const opportunityCount = useOpportunities((s) => s.entriesByWorkshop[wsId]?.length ?? 0);
  const recommendationCount = useRecommendations(
    (s) => s.entriesByWorkshop[wsId]?.length ?? 0,
  );
  const noteCount = useFacilitationNotes((s) => s.entriesByWorkshop[wsId]?.length ?? 0);

  const [tab, setTab] = useState<LogTab>("core");
  const [pendingFocusId, setPendingFocusId] = useState<string | null>(null);

  // Honour a search / traceability jump: pick the right tab, then scroll
  // the matching row once that tab's list has painted.
  useEffect(() => {
    if (!open) {
      setPendingFocusId(null);
      return;
    }
    const pending = useJumpTarget.getState().target;
    if (!pending || pending.workshopId !== wsId) return;
    const nextTab = logTabForCategory(pending.category);
    if (!nextTab) return; // upload/capture/transcript — leave for their owners
    useJumpTarget.getState().clear();
    setTab(nextTab);
    setPendingFocusId(pending.id);
  }, [open, wsId]);

  useEffect(() => {
    if (!open || !pendingFocusId) return;
    const id = pendingFocusId;
    const handle = window.setTimeout(() => {
      const el = document.querySelector<HTMLElement>(
        `[data-record-id="${CSS.escape(id)}"]`,
      );
      setPendingFocusId(null);
      if (!el) return;
      el.scrollIntoView({ block: "nearest", behavior: "smooth" });
      el.style.outline = "2px solid var(--accent-sky)";
      el.style.outlineOffset = "2px";
      window.setTimeout(() => {
        el.style.outline = "";
        el.style.outlineOffset = "";
      }, 1600);
    }, 50);
    return () => window.clearTimeout(handle);
  }, [open, tab, pendingFocusId]);

  const badgeText =
    tab === "core"
      ? `${decisions.length} decisions · ${actions.length} actions · ${assumptions.length} assumptions`
      : tab === "knowledge"
        ? `${findingCount} findings · ${opportunityCount} opportunities · ${recommendationCount} recommendations`
        : `${noteCount} notes`;

  // ── New-decision form state ──
  const [dTitle, setDTitle] = useState("");
  const [dRationale, setDRationale] = useState("");
  const [dOwner, setDOwner] = useState("");
  const [dTags, setDTags] = useState<string[]>([]);

  function submitDecision(): void {
    const title = dTitle.trim();
    if (!title) return;
    addDecision(wsId, {
      id: crypto.randomUUID(),
      workshopId: wsId,
      title,
      rationale: dRationale.trim(),
      owner: dOwner.trim(),
      status: "proposed",
      createdAt: Date.now(),
      addedBy,
      tags: dTags.length ? dTags : undefined,
    });
    setDTitle("");
    setDRationale("");
    setDOwner("");
    setDTags([]);
  }

  // ── New-action form state ──
  const [aTitle, setATitle] = useState("");
  const [aOwner, setAOwner] = useState("");
  const [aDue, setADue] = useState("");
  const [aTags, setATags] = useState<string[]>([]);

  function submitAction(): void {
    const title = aTitle.trim();
    if (!title) return;
    addAction(wsId, {
      id: crypto.randomUUID(),
      workshopId: wsId,
      title,
      owner: aOwner.trim(),
      due: aDue.trim(),
      status: "open",
      createdAt: Date.now(),
      addedBy,
      tags: aTags.length ? aTags : undefined,
    });
    setATitle("");
    setAOwner("");
    setADue("");
    setATags([]);
  }

  // ── New-assumption form state ──
  const [asmStatement, setAsmStatement] = useState("");
  const [asmBasis, setAsmBasis] = useState("");
  const [asmOwner, setAsmOwner] = useState("");
  const [asmTags, setAsmTags] = useState<string[]>([]);

  function submitAssumption(): void {
    const statement = asmStatement.trim();
    if (!statement) return;
    addAssumption(wsId, {
      id: crypto.randomUUID(),
      workshopId: wsId,
      statement,
      basis: asmBasis.trim(),
      owner: asmOwner.trim(),
      status: "assumed",
      createdAt: Date.now(),
      addedBy,
      tags: asmTags.length ? asmTags : undefined,
    });
    setAsmStatement("");
    setAsmBasis("");
    setAsmOwner("");
    setAsmTags([]);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <PanelDialogContent
        className="w-[90vw] max-w-[900px] border-0 bg-transparent p-0 shadow-none"
        style={{ outline: "none" }}
        // Escape while renaming a row cancels the rename, not the whole modal.
        onEscapeKeyDown={(e) => {
          if (document.activeElement?.hasAttribute("data-inline-edit")) e.preventDefault();
        }}
      >
        <DialogTitle className="sr-only">
          Workshop Log — {workshopId}
        </DialogTitle>
        <DialogDescription className="sr-only">
          Capture and track key decisions (with rationale), action items, and
          working assumptions for this workshop.
        </DialogDescription>
        <div className="modal-shell" style={{ width: "100%", maxHeight: "85vh" }}>
          {/* HEADER */}
          <div className="modal-header">
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <Scale
                aria-hidden
                style={{ width: 16, height: 16, color: "var(--accent-true-blue)" }}
              />
              <span className="modal-title">WORKSHOP LOG</span>
              <WorkshopLabelPill workshopId={workshopId} label={badgeText} />
            </div>
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="modal-close-btn"
              aria-label="Close"
            >
              <X aria-hidden style={{ width: 12, height: 12 }} />
              CLOSE
            </button>
          </div>

          {/* TAB BAR (LOG-TABS) — registers grouped into three tabs with
              WorkshopRepository as the canonical read-through SSoT. */}
          <div className="dt-tab-bar" role="tablist" aria-label="Workshop Log sections">
            {LOG_TABS.map((t) => (
              <button
                key={t.key}
                type="button"
                role="tab"
                aria-selected={tab === t.key}
                onClick={() => setTab(t.key)}
                className={cn("dt-tab", tab === t.key && "dt-tab-active")}
              >
                {t.label}
              </button>
            ))}
          </div>

          {/* BODY */}
          <div className="modal-body">
            <div
              className="dt-pane dt-pane-active"
              style={{ display: "flex", flexDirection: "column", gap: 22 }}
            >
              {tab === "core" && (
                <>
              {/* ── Decisions ── */}
              <section>
                <SectionLabel icon={ClipboardCheck} text="Decisions" />
                <div className="flex flex-col gap-2 pt-2">
                  <input
                    aria-label="Decision title"
                    type="text"
                    value={dTitle}
                    onChange={(e) => setDTitle(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") submitDecision();
                    }}
                    placeholder="Decision — e.g. Adopt on-the-loop as the default mode"
                    className={FIELD_CLASS}
                  />
                  <div className="flex flex-wrap gap-2">
                    <input
                      aria-label="Decision rationale"
                      type="text"
                      value={dRationale}
                      onChange={(e) => setDRationale(e.target.value)}
                      placeholder="Rationale — why?"
                      className={cn(FIELD_CLASS, "min-w-0 flex-1")}
                    />
                    <OwnerCombobox
                      ariaLabel="Decision owner"
                      value={dOwner}
                      onChange={setDOwner}
                      options={ownerOptions}
                      className="w-full sm:w-[150px] sm:flex-none"
                    />
                    <AddButton onClick={submitDecision} disabled={!dTitle.trim()} />
                  </div>
                  <TagInput
                    value={dTags}
                    onChange={setDTags}
                    suggestions={collectTags(decisions)}
                    workshopId={wsId}
                    ariaLabel="Decision tags"
                  />
                </div>
                <ul className="flex flex-col gap-1.5 pt-3">
                  {decisions.length === 0 && <EmptyHint text="No decisions logged yet." />}
                  {decisions.map((d) => (
                    <li key={d.id} data-record-id={d.id} style={ROW_CARD}>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <EditableTitle
                          value={d.title}
                          label="decision title"
                          onSave={(title) => updateDecision(wsId, d.id, { title })}
                          style={ROW_TITLE}
                        />
                        {d.rationale && <div style={ROW_SUB}>{d.rationale}</div>}
                        <div style={ROW_META}>
                          {d.owner ? `${d.owner} · ` : ""}
                          {d.addedBy}
                        </div>
                        <RowTags tags={d.tags} workshopId={wsId} />
                      </div>
                      <StatusSelect
                        ariaLabel={`Status of decision: ${d.title}`}
                        value={d.status}
                        onValueChange={(v) =>
                          updateDecision(wsId, d.id, { status: v as DecisionStatus })
                        }
                        options={DECISION_STATUSES}
                        accentFor={statusAccent}
                        className="w-[124px] flex-none"
                      />
                      <RemoveButton
                        label={`Remove decision: ${d.title}`}
                        onClick={() => removeDecision(wsId, d.id)}
                      />
                    </li>
                  ))}
                </ul>
              </section>

              {/* ── Actions ── */}
              <section>
                <SectionLabel icon={ListChecks} text="Actions" />
                <div className="flex flex-col gap-2 pt-2">
                  <input
                    aria-label="Action title"
                    type="text"
                    value={aTitle}
                    onChange={(e) => setATitle(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") submitAction();
                    }}
                    placeholder="Action — e.g. Refine escalation criteria"
                    className={FIELD_CLASS}
                  />
                  <div className="flex flex-wrap gap-2">
                    <OwnerCombobox
                      ariaLabel="Action owner"
                      value={aOwner}
                      onChange={setAOwner}
                      options={ownerOptions}
                      className="w-full sm:w-[150px] sm:flex-none"
                    />
                    <DueDatePicker
                      ariaLabel="Action due date"
                      value={aDue}
                      onChange={setADue}
                      className="w-full sm:w-[160px] sm:flex-none"
                    />
                    <AddButton onClick={submitAction} disabled={!aTitle.trim()} />
                  </div>
                  <TagInput
                    value={aTags}
                    onChange={setATags}
                    suggestions={collectTags(actions)}
                    workshopId={wsId}
                    ariaLabel="Action tags"
                  />
                </div>
                <ul className="flex flex-col gap-1.5 pt-3">
                  {actions.length === 0 && <EmptyHint text="No actions captured yet." />}
                  {actions.map((a) => (
                    <li key={a.id} data-record-id={a.id} style={ROW_CARD}>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <EditableTitle
                          value={a.title}
                          label="action title"
                          onSave={(title) => updateAction(wsId, a.id, { title })}
                          style={ROW_TITLE}
                        />
                        <div style={ROW_META}>
                          {a.owner ? `${a.owner} · ` : ""}
                          {a.due ? `due ${formatDueDate(a.due)} · ` : ""}
                          {a.addedBy}
                        </div>
                        <RowTags tags={a.tags} workshopId={wsId} />
                      </div>
                      <StatusSelect
                        ariaLabel={`Status of action: ${a.title}`}
                        value={a.status}
                        onValueChange={(v) =>
                          updateAction(wsId, a.id, { status: v as ActionStatus })
                        }
                        options={ACTION_STATUSES}
                        accentFor={statusAccent}
                        className="w-[124px] flex-none"
                      />
                      <RemoveButton
                        label={`Remove action: ${a.title}`}
                        onClick={() => removeAction(wsId, a.id)}
                      />
                    </li>
                  ))}
                </ul>
              </section>

              {/* ── Assumptions ── */}
              <section>
                <SectionLabel icon={FlaskConical} text="Assumptions" />
                <div className="flex flex-col gap-2 pt-2">
                  <input
                    aria-label="Assumption statement"
                    type="text"
                    value={asmStatement}
                    onChange={(e) => setAsmStatement(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") submitAssumption();
                    }}
                    placeholder="Assumption — e.g. Participants can self-serve the mode matrix"
                    className={FIELD_CLASS}
                  />
                  <div className="flex flex-wrap gap-2">
                    <input
                      aria-label="Assumption basis"
                      type="text"
                      value={asmBasis}
                      onChange={(e) => setAsmBasis(e.target.value)}
                      placeholder="Basis — what it rests on"
                      className={cn(FIELD_CLASS, "min-w-0 flex-1")}
                    />
                    <OwnerCombobox
                      ariaLabel="Assumption owner"
                      value={asmOwner}
                      onChange={setAsmOwner}
                      options={ownerOptions}
                      className="w-full sm:w-[150px] sm:flex-none"
                    />
                    <AddButton
                      onClick={submitAssumption}
                      disabled={!asmStatement.trim()}
                    />
                  </div>
                  <TagInput
                    value={asmTags}
                    onChange={setAsmTags}
                    suggestions={collectTags(assumptions)}
                    workshopId={wsId}
                    ariaLabel="Assumption tags"
                  />
                </div>
                <ul className="flex flex-col gap-1.5 pt-3">
                  {assumptions.length === 0 && (
                    <EmptyHint text="No assumptions logged yet." />
                  )}
                  {assumptions.map((a) => (
                    <li key={a.id} data-record-id={a.id} style={ROW_CARD}>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <EditableTitle
                          value={a.statement}
                          label="assumption statement"
                          onSave={(statement) =>
                            updateAssumption(wsId, a.id, { statement })
                          }
                          style={ROW_TITLE}
                        />
                        {a.basis && <div style={ROW_SUB}>{a.basis}</div>}
                        <div style={ROW_META}>
                          {a.owner ? `${a.owner} · ` : ""}
                          {a.addedBy}
                        </div>
                        <RowTags tags={a.tags} workshopId={wsId} />
                      </div>
                      <StatusSelect
                        ariaLabel={`Status of assumption: ${a.statement}`}
                        value={a.status}
                        onValueChange={(v) =>
                          updateAssumption(wsId, a.id, { status: v as AssumptionStatus })
                        }
                        options={ASSUMPTION_STATUSES}
                        accentFor={statusAccent}
                        className="w-[124px] flex-none"
                      />
                      <RemoveButton
                        label={`Remove assumption: ${a.statement}`}
                        onClick={() => removeAssumption(wsId, a.id)}
                      />
                    </li>
                  ))}
                </ul>
              </section>
                </>
              )}

              {tab === "knowledge" && (
                <>
                  <FindingsSection
                    workshopId={wsId}
                    addedBy={addedBy}
                    ownerOptions={ownerOptions}
                  />
                  <OpportunitiesSection
                    workshopId={wsId}
                    addedBy={addedBy}
                    ownerOptions={ownerOptions}
                  />
                  <RecommendationsSection
                    workshopId={wsId}
                    addedBy={addedBy}
                    ownerOptions={ownerOptions}
                  />
                  <AiExtractionSection
                    workshopId={wsId}
                    addedBy={addedBy}
                    ownerOptions={ownerOptions}
                  />
                </>
              )}

              {tab === "notes" && (
                <NotesSection
                  workshopId={wsId}
                  addedBy={addedBy}
                  ownerOptions={ownerOptions}
                />
              )}

              <p className="text-muted-foreground pt-1 text-fs-label tracking-wider uppercase">
                Saved locally — survives reload. Decisions carry their rationale
                so the team can answer "why did we decide this?".
              </p>
            </div>
          </div>

          {/* FOOTER */}
          <div className="modal-footer">
            <span className="modal-footer-label">XAES Phase Zero</span>
            <span className="modal-footer-sep">|</span>
            <span className="modal-footer-ws">Decision Traceability</span>
            <span className="modal-footer-sep">|</span>
            <span className="modal-footer-ws" style={{ color: workshopLabelColor(workshopId) }}>
              {workshopId}
            </span>
          </div>
        </div>
      </PanelDialogContent>
    </Dialog>
  );
}


